import React, { useState, useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import * as actions from '../../../redux/actions'

import Dropdown from 'components/Dropdown'
import Logo from 'components/Logo'
import Row from '../../../components/Row'
import NotificationStep from 'components/NotificationStep'
import { Typography, Column, Container, TextInput } from 'components'

import * as routes from 'settings/_routesSettings'
import axios from 'settings/axios'
import { apiEndpoints } from 'settings/_apiSettings'
import { planGroupsSettings } from 'settings/_planGroupSettings'
import { COUNTRY_STATES } from 'settings/countryStatesSettings'
import InputValidation from '../../../utils/InputValidation'
import { AWAITING_MEDICAL_REQUEST } from 'settings/_personSettings'

import Modal from 'containers/ModalManager/Modals/Modal'

import { ReactComponent as CidImage } from 'static/svg/cid-image.svg'
import { SignupContext } from 'contexts/SignupContext'

const AttachMedicalRequest = () => {
  const history = useHistory()
  const location = useLocation()
  const isSignup = location.pathname === routes.REGISTER_SUCCESS_ATTACH_MEDICAL_REQUEST
  const isSuccess = location.pathname === routes.REGISTER_ATTACH_MEDICAL_REQUEST_DONE
  const dispatch = useDispatch()
  const userStatus = useSelector(state => state.auth.profileSelected?.status_code)
  const userPlanGroup = useSelector(state => state.auth.planSelected?.plan_group)
  const userFirstName = useSelector(state => state.auth.userData?.person_general?.first_name)

  const { daysToAuthorizeProcedure } = useContext(SignupContext)

  const planGroupSlug = userPlanGroup?.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(' ', '-').toLowerCase()
  const medicalRequestURL = `https://conteudo.telavita.com.br/solicitar-pedido-medico-online-${planGroupSlug}`

  const [isLoading, setIsLoading] = useState(false)
  const [readyToSubmit, setReadyToSubmit] = useState(false)

  const [groupPlans, setGroupPlans] = useState({})
  const [planSettings, setPlanSettings] = useState({})

  const [medicalRequest, setMedicalRequest] = useState({
    isValid: false,
    message: '',
    value: '',
    fileName: '',
  })

  const [cid, setCid] = useState({
    value: null,
    isValid: false
  })

  const [crm, setCrm] = useState({
    value: null,
    isValid: false,
  })

  const [uf, setUf] = useState(null)

  const [showModal, setShowModal] = useState(false)

  const closeModal = () => { dispatch(actions.hideModal()) }

  useEffect(() => {
    if (userStatus !== AWAITING_MEDICAL_REQUEST) {
      history.push(routes.DASHBOARD_APPOINTMENTS)
    }

  }, [userStatus, history, location])

  useEffect(() => {
    handleGroupPlans(userPlanGroup)
  }, [userPlanGroup])

  useEffect(() => {
    const isReady = (
      (cid.isValid && crm.isValid && medicalRequest.isValid && uf !== null)
      || (medicalRequest.isValid && !Object.keys(planSettings).includes('diseaseInfo', 'doctorInfo'))
    )

    setReadyToSubmit(isReady)
  }, [
    crm,
    cid,
    uf,
    medicalRequest,
    userPlanGroup,
    planSettings
  ])

  const handleGroupPlans = (groupPlan) => {
    setGroupPlans(groupPlan)
    setPlanSettings(planGroupsSettings[groupPlan?.code])
  }

  const handleMedicalRequestInput = async e => {
    const file = e.target.files.length > 0 ? e.target.files[0] : null

    if (file !== null) {
      const fileName = file.name
      const validation = new InputValidation(e.target.files, 'imageOrPdf')
      const imageIsValid = await validation.validate()

      setMedicalRequest({
        isValid: imageIsValid.success,
        message: imageIsValid.message,
        value: imageIsValid.encodedFile,
        fileName: fileName,
      })
    }
  }

  const handleCidInput = e => {
    let value = e.target.value
    let validation = new InputValidation(value, 'cid').validate()

    setCid({
      value,
      isValid: validation.success,
      message: validation.message
    })
  }

  const handleCrmInput = e => {
    const value = e.target.value
    const validation = new InputValidation(value, 'integer').validate()

    setCrm(prevState => ({
      ...prevState,
      value,
      isValid: validation.success,
      message: validation.message,
    }))
  }

  const onSubmit = async () => {
    setIsLoading(true)

    try {
      const payload = {
        medical_request: medicalRequest.value,
        cid: cid.value !== null ? cid.value : '',
        professional_crm_code: crm.value !== null ? crm.value : '',
        professional_crm_uf: uf !== null ? uf : ''
      }

      const response = await axios.post(apiEndpoints.PATIENTS_MEDICAL_REQUEST, payload)

      if(response.status === 'OK'){
        history.push(routes.REGISTER_ATTACH_MEDICAL_REQUEST_DONE)
      }
    } catch (err) {
      dispatch(actions.setGenericError(err.message))
      /**
       * TODO => se for 401, redirecionar para a tela de login
       * se for 403, mostrar erro de permissão?
       * se for 404, mostrar o erro do backend? Nenhum plano associado ao perfil do paciente foi encontrado.
       * */
    }
  }

  return (
    <>
      <Container>
        <Row justify='center' className='Register__Header'>
          <Column align='center' justify='center' colWidth={4}>
            <Column style={{ textAlign: 'center' }}>
              <Logo
                fill='#1f35b5'
                onClickLogo={() => (window.location.href = process.env.REACT_APP_INSTITUTIONAL_URL)}
              />
            </Column>
          </Column>
        </Row>

        <Row justify='center' className="Register__boxMedicalRequest" >
          <NotificationStep
            buttonDisabled={!readyToSubmit || isLoading}
            btnText='Enviar e prosseguir'
            loading={isLoading}
            iconColor='#4ac326'
            iconName='Check'
            hasIcon={isSignup || isSuccess}
            hasButton={!isSuccess}
            onSuccessClick={() => onSubmit()}
            mainText={
              isSuccess ? (
                <>
                  <Row justify='center'>
                    <Typography
                      align='center'
                      className="Register__boxMedicalRequest--info"
                      type='custom'
                    >
                      Pedido médico será analisado! Agora é só <strong>aguardar</strong> a autorização do seu convênio.
                    </Typography>
                  </Row>
                  <Row margin='30px 0' justify='center'>
                    <Typography align='center' className="Register__boxMedicalRequest--authorization" style={{ maxWidth: '323px' }}>
                    Pediremos a autorização junto ao seu convênio e <strong>em até {daysToAuthorizeProcedure} dias úteis</strong> você receberá a confirmação por e-mail e SMS.
                    </Typography>
                  </Row>
                  <Row justify='center'>
                    <Typography align='center' style={{ lineHeight: '1.9rem' }}>
                      Enquanto você aguarda a aprovação, ainda não é possível agendar consultas,
                      mas você já pode <Link to={routes.HOME_PSIC}><strong>conhecer nossos profissionais</strong></Link>.
                    </Typography>
                  </Row>
                </>
              ) : (
                <>
                  {isSignup &&
                    <Row justify='center'>
                      <Typography align='center' type='heading2'>
                        Conta criada com sucesso!
                      </Typography>
                    </Row>
                  }

                  <Row justify='center'>
                    <div
                      className='Register__InfoGroup__Head__Image'
                      style={{ margin: '30px 0 20px 0' }}
                    >
                      <img src={groupPlans?.logo_url ?? ''} alt={groupPlans?.name ?? ''} />
                    </div>

                    <Typography align='center' type='heading5' style={{ marginBottom: 10, lineHeight: '1.9rem' }}>
                      <strong>
                        Para agendar uma consulta, seu convênio requer um pedido médico, {
                          userFirstName
                        }
                      </strong>
                    </Typography>

                    {groupPlans.code === 'CASSI' 
                      ? null 
                      : (
                        <Typography className='Register__no-medical-order' align='center' type='heading6'>
                          Não tem um pedido médico? <a href={`${medicalRequestURL}`} target='_blank' rel="noopener noreferrer">Clique aqui</a>
                        </Typography>
                      )
                    }

                  </Row>

                  <Row>
                    <div className='Register__InfoGroup__Forms__Item inputMedicalRequest'>
                      <div className='Register__InfoGroup__Forms--image-wrapper'>
                        <TextInput
                          addIcon={true}
                          placeholder="Anexar pedido médico"
                          name='medic_request'
                          iconProps={{
                            fill: '#898989',
                            height: '20px',
                            name: 'UploadFile',
                            width: '20px'
                          }}
                          inputHeight='4.8rem'
                          hasError={!medicalRequest.isValid && medicalRequest.message !== ''}
                          messageError={medicalRequest.message}
                          value={medicalRequest.fileName}
                          readOnly
                        />
                        <input
                          id='hiddenInput-file'
                          type='file'
                          onChange={e => handleMedicalRequestInput(e)}
                          accept='.jpg,.jpeg,.png,.bmp,.pdf'
                        />
                      </div>
                      {planSettings && planSettings.medicalRequest?.helperText && (
                        <div className='Register__InfoGroup__Forms__Item__HelperText helperTextMedicalRequest'>
                          *Caso não possua, procure um médico e obtenha o encaminhamento.
                        </div>
                      )}
                    </div>

                    {planSettings?.diseaseInfo && planSettings?.diseaseInfo.cid && (
                      <div className='Register__InfoGroup__Forms__Item inputMedicalRequest'>
                        <TextInput
                          id='cid'
                          maxLength={10}
                          placeholder='CID (letras e números)'
                          onInputChange={handleCidInput}
                          messageError={cid.message}
                          hasError={!cid.isValid && cid.value !== null}
                          addIcon
                          iconProps={{
                            height: '24',
                            name: 'Help',
                            width: '24',
                          }}
                          onClickIcon={() => setShowModal(true)}
                        />
                        {planSettings && planSettings?.diseaseInfo?.helperText && (
                          <div className='Register__InfoGroup__Forms__Item__HelperText helperTextMedicalRequest'>
                             *Consta no pedido médico. Digite apenas a letra e os números que aparecem depois do hífen no CID (Código Internacional de Doenças).
                          </div>
                        )}
                      </div>
                    )}
                    {planSettings.doctorInfo && planSettings.doctorInfo.crm && (
                      <div className='Register__InfoGroup__Forms__Item inputMedicalRequest'>
                        <TextInput
                          id='crm_number'
                          maxLength={10}
                          placeholder='Número do CRM'
                          onInputChange={handleCrmInput}
                          value={crm.value}
                          hasError={!crm.isValid && crm.value !== null}
                          messageError={crm.message}
                        />
                      </div>
                    )}
                    {planSettings.doctorInfo && planSettings.doctorInfo.uf && (
                      <div className='Register__InfoGroup__Forms__Item inputMedicalRequest'>
                        <Dropdown
                          name='plan'
                          options={COUNTRY_STATES}
                          onSelect={value => setUf(value)}
                          placeholder='UF do CRM (2 letras)'
                          dropDownOverflow
                          dropDownItensToShow='3'
                        />
                        <div className='Register__InfoGroup__Forms__Item__HelperText helperTextMedicalRequest'>
                          *Os dois campos acima são dados do médico que prescreveu a terapia, e devem
                          constar no pedido médico.
                        </div>
                      </div>
                    )}
                  </Row>
                </>
              )
            }
          />
        </Row>
      </Container>

      {
        showModal &&
          <Modal
            btnText='Fechar'
            handleClick={() => {
              closeModal()
              setShowModal(false)
            }}
            hasButton={true}
            modalTitle='Exemplo de CID'
            onClose={ () => setShowModal(false) }
          >
            <CidImage />

            <Typography>
              <strong>Digite apenas a letra e os números que aparecem depois do hífen no CID</strong>
              {' '}(Código Internacional de Doenças).
            </Typography>
            <Typography>
              Essa informação deve constar no pedido médico. Caso não encontre, procure um médico e obtenha um novo encaminhamento.
            </Typography>
          </Modal>
      }
    </>
  )
}

export default AttachMedicalRequest
